/**
 * History Store — per-file snapshot list, scoped to the current workspace.
 *
 * Snapshots are loaded lazily on first access per filePath. The store mirrors
 * authoritative Engine state via `event/history.*` notifications and provides
 * thin RPC wrappers for mutations (snapshot / restore / delete / tag).
 *
 * Used by:
 * - Sidebar Explorer "History" section (file-level)
 * - Time-Machine preview overlay
 */

import type {
    HistoryRestoreResult,
    SnapshotMeta,
    SnapshotTrigger,
} from "@magic-md/engine-client/protocol";
import { create } from "zustand";

import { getPlatformService } from "../platform";

export type { SnapshotMeta, SnapshotTrigger };

interface FileHistory {
    snapshots: SnapshotMeta[];
    total: number;
    loading: boolean;
    /** Most recent error from a load attempt (cleared on next success) */
    error?: string;
    /** Has loadIfNeeded ever resolved (success or empty) for this path */
    loaded: boolean;
}

interface HistoryState {
    /** filePath → FileHistory */
    files: Record<string, FileHistory>;

    /** Load snapshots for a file if not loaded yet (no-op if loading or loaded). */
    loadIfNeeded(filePath: string): Promise<void>;

    /** Force-refresh a file's snapshot list. */
    refresh(filePath: string): Promise<void>;

    /** Take a snapshot. Returns the meta on success (or existing one if deduped). */
    take(input: {
        filePath: string;
        message?: string;
        tag?: string;
        trigger?: SnapshotTrigger;
        content?: string;
    }): Promise<SnapshotMeta | null>;

    /** Restore document content from a snapshot. */
    restore(input: {
        filePath: string;
        snapshotId: string;
        currentContent: string;
    }): Promise<HistoryRestoreResult | null>;

    /** Delete a snapshot. */
    remove(filePath: string, snapshotId: string): Promise<boolean>;

    /** Set / clear a tag on a snapshot. */
    tag(filePath: string, snapshotId: string, tag: string | null): Promise<SnapshotMeta | null>;

    /** Get cached snapshots for a path (does not trigger a load). */
    getForPath(filePath: string): SnapshotMeta[];

    /** Get sorted snapshot list for a file (newest first). Used by snapshot preview navigation. */
    getSnapshotList(filePath: string): SnapshotMeta[];

    /** Get the previous/next snapshot in the list. Returns null if at boundary or snapshot not found. */
    getAdjacentSnapshot(filePath: string, currentSnapshotId: string, direction: "prev" | "next"): SnapshotMeta | null;

    /** Internal: replace snapshots for a file. */
    _setSnapshots(filePath: string, snapshots: SnapshotMeta[], total: number): void;

    /** Internal: merge a single snapshot (insert or update). */
    _upsertSnapshot(filePath: string, snapshot: SnapshotMeta): void;

    /** Internal: drop a snapshot from cache. */
    _removeSnapshot(filePath: string, snapshotId: string): void;
}

const EMPTY_FILE: FileHistory = {
    snapshots: [],
    total: 0,
    loading: false,
    loaded: false,
};

function ensureFile(state: HistoryState, filePath: string): FileHistory {
    return state.files[filePath] ?? EMPTY_FILE;
}

export const useHistoryStore = create<HistoryState>((set, get) => ({
    files: {},

    async loadIfNeeded(filePath) {
        const file = ensureFile(get(), filePath);
        if (file.loading || file.loaded) return;
        await get().refresh(filePath);
    },

    async refresh(filePath) {
        // Mark loading
        set((state) => ({
            files: {
                ...state.files,
                [filePath]: {
                    ...ensureFile(state, filePath),
                    loading: true,
                    error: undefined,
                },
            },
        }));

        try {
            const result = await getPlatformService().engine.historyList({ filePath });
            set((state) => ({
                files: {
                    ...state.files,
                    [filePath]: {
                        snapshots: [...result.snapshots],
                        total: result.total,
                        loading: false,
                        loaded: true,
                    },
                },
            }));
        } catch (err) {
            set((state) => ({
                files: {
                    ...state.files,
                    [filePath]: {
                        ...ensureFile(state, filePath),
                        loading: false,
                        loaded: true,
                        error: err instanceof Error ? err.message : String(err),
                    },
                },
            }));
        }
    },

    async take(input) {
        try {
            const result = await getPlatformService().engine.historySnapshot({
                filePath: input.filePath,
                message: input.message,
                tag: input.tag,
                trigger: input.trigger ?? "manual",
                content: input.content,
            });
            if (result.snapshot) {
                get()._upsertSnapshot(input.filePath, result.snapshot);
                return result.snapshot;
            }
            return null;
        } catch {
            return null;
        }
    },

    async restore(input) {
        try {
            const result = await getPlatformService().engine.historyRestore({
                filePath: input.filePath,
                snapshotId: input.snapshotId,
                currentContent: input.currentContent,
            });
            // Merge any pre-snapshot that was created
            if (result.preSnapshot) {
                get()._upsertSnapshot(input.filePath, result.preSnapshot);
            }
            return result;
        } catch {
            return null;
        }
    },

    async remove(filePath, snapshotId) {
        try {
            const result = await getPlatformService().engine.historyDelete({
                filePath,
                snapshotId,
            });
            if (result.removed) {
                get()._removeSnapshot(filePath, snapshotId);
            }
            return result.removed;
        } catch {
            return false;
        }
    },    async tag(filePath, snapshotId, tag) {
        try {
            const result = await getPlatformService().engine.historyTag({
                filePath,
                snapshotId,
                tag,
            });
            if (result.snapshot) {
                get()._upsertSnapshot(filePath, result.snapshot);
                return result.snapshot;
            }
            return null;
        } catch {
            return null;
        }
    },

    getForPath(filePath) {
        return ensureFile(get(), filePath).snapshots;
    },

    getSnapshotList(filePath) {
        // Return all snapshots for a file, sorted newest first (already maintained by _upsertSnapshot)
        return [...ensureFile(get(), filePath).snapshots];
    },

    getAdjacentSnapshot(filePath, currentSnapshotId, direction) {
        const snapshots = ensureFile(get(), filePath).snapshots;
        const currentIndex = snapshots.findIndex((s) => s.id === currentSnapshotId);
        if (currentIndex === -1) return null;

        if (direction === "prev") {
            // Previous = newer (earlier in list)
            return currentIndex > 0 ? snapshots[currentIndex - 1] : null;
        } else {
            // Next = older (later in list)
            return currentIndex < snapshots.length - 1 ? snapshots[currentIndex + 1] : null;
        }
    },

    _setSnapshots(filePath, snapshots, total) {
        set((state) => ({
            files: {
                ...state.files,
                [filePath]: {
                    ...ensureFile(state, filePath),
                    snapshots: [...snapshots],
                    total,
                    loading: false,
                    loaded: true,
                },
            },
        }));
    },

    _upsertSnapshot(filePath, snapshot) {
        set((state) => {
            const file = ensureFile(state, filePath);
            const next = [...file.snapshots];
            const idx = next.findIndex((s) => s.id === snapshot.id);
            if (idx >= 0) {
                next[idx] = snapshot;
            } else {
                // Insert maintaining newest-first order (createdAt DESC)
                let insertAt = next.findIndex((s) => s.createdAt < snapshot.createdAt);
                if (insertAt < 0) insertAt = next.length;
                next.splice(insertAt, 0, snapshot);
            }
            return {
                files: {
                    ...state.files,
                    [filePath]: {
                        ...file,
                        snapshots: next,
                        total: idx >= 0 ? file.total : file.total + 1,
                        loaded: true,
                    },
                },
            };
        });
    },

    _removeSnapshot(filePath, snapshotId) {
        set((state) => {
            const file = ensureFile(state, filePath);
            const next = file.snapshots.filter((s) => s.id !== snapshotId);
            return {
                files: {
                    ...state.files,
                    [filePath]: {
                        ...file,
                        snapshots: next,
                        total: Math.max(0, file.total - (file.snapshots.length - next.length)),
                    },
                },
            };
        });
    },
}));
